import { useQuery } from '@tanstack/react-query';
import { SURVEY_STATUSES } from 'constants/surveyStatuses';
import { Form, Formik, FormikErrors, useField, useFormikContext } from 'formik';
import { incidenceTypeQueries } from 'hooks/backend/incidenceTypes';
import { useSaveSurvey } from 'hooks/backend/surveys';
import { useCreateTag, useTags } from 'hooks/backend/tags';
import { useSubmitValidation } from 'hooks/forms';
import { useEffect, useState } from 'react';
import { getNestedErrorMessages } from 'util/forms';
import { useOrderedOrganizations } from '../../hooks/backend/organizations';
import { useOrderedTeamMembers } from '../../hooks/backend/team';
import { useAllUsers } from '../../hooks/backend/users';
import { useHasRole } from '../../hooks/users';
import { UpdateSurveyRequestData } from '../../services/backend/surveys';
import {
  IncidenceType,
  Question,
  Survey,
  SURVEY_TYPE,
  SurveyWave,
  Tag,
  TeamMember,
  UserV2,
} from '../../types/domainModels';
import { ReactSelectValue } from '../../types/forms';
import { getIncidenceTypeOptions } from '../../util/incidence';
import { showErrorMessage } from '../../util/notifications';
import { createTeamMemberOption } from '../../util/team';
import { getOrgStatus } from '../../util/users';

import FormErrorsAlert from 'components/common/forms/FormErrorsAlert';
import PutSurveyIntoDraftDialog from 'components/common/PutSurveyIntoDraftDialog';
import UnsavedChangesModal from 'components/common/UnsavedChangesModal';
import ButtonLoading from '../common/forms/ButtonLoading';
import FormInput from '../common/forms/FormInput';
import FormSearchSelectInput from '../common/forms/FormSearchSelectInput';
import SkeletonSurveyCard from './SkeletonSurveyCard';
import SurveyStepStickyHeader from './SurveyStepStickyHeader';

type OverviewFormProps = {
  incidenceTypes: ReactSelectValue<IncidenceType>[];
  isAdmin: boolean;
  isSavingSurvey: boolean;
  isShowingUnsavedChanges: boolean;
  onDiscardChanges(): void;
  onDismissUnsavedChanges(): void;
  onHasError(): void;
  onOverviewDirtyChanged(isDirty: boolean): void;
  organizations: ReactSelectValue<number>[];
  questions: Question[];
  users: UserV2[];
  waves: SurveyWave[];
};

interface SurveyTypeOption {
  value: SURVEY_TYPE;
  label: string;
}

interface SurveyOverviewFormData {
  contact: ReactSelectValue<TeamMember> | null;
  glassContact: ReactSelectValue<UserV2> | null;
  incidenceType: ReactSelectValue<IncidenceType> | null;
  organizationId: ReactSelectValue<number> | null;
  respondents: number | '';
  surveyType: ReactSelectValue<SurveyTypeOption> | null;
  tag: ReactSelectValue<Tag> | null;
  title: string;
}

interface SurveyOverviewFormDataValidated {
  contact: ReactSelectValue<TeamMember> | null;
  glassContact: ReactSelectValue<UserV2> | null;
  incidenceType: ReactSelectValue<IncidenceType>;
  organizationId: ReactSelectValue<number>;
  respondents: number;
  surveyType: ReactSelectValue<SurveyTypeOption> | null;
  tag: ReactSelectValue<Tag> | null;
  title: string;
}

const SURVEY_TYPE_OPTIONS: ReactSelectValue<SurveyTypeOption>[] = [
  {
    value: { value: SURVEY_TYPE.FULL_SERVICE, label: 'Full Service' },
    label: 'Full Service',
  },
  {
    value: { value: SURVEY_TYPE.HYBRID, label: 'Hybrid' },
    label: 'Hybrid',
  },
  {
    value: { value: SURVEY_TYPE.DIY, label: 'DIY' },
    label: 'DIY',
  },
];

function createSurveyTypeOption(
  surveyType: SURVEY_TYPE,
): ReactSelectValue<SurveyTypeOption> {
  const option = SURVEY_TYPE_OPTIONS.find(
    (opt) => opt.value.value === surveyType,
  );
  return (
    option || {
      value: { value: surveyType, label: surveyType },
      label: surveyType,
    }
  );
}

function createUserOption(user: UserV2): ReactSelectValue<UserV2> {
  return {
    label: `${user.firstName} ${user.lastName}`,
    value: user,
  };
}

const OverviewStep = ({
  isShowingUnsavedChanges,
  onDiscardChanges,
  onDismissUnsavedChanges,
  onHasError,
  onOverviewDirtyChanged,
  onOverviewSaved,
  questions,
  survey,
  waves,
}: {
  isShowingUnsavedChanges: boolean;
  onDiscardChanges(): void;
  onDismissUnsavedChanges(): void;
  onHasError(): void;
  onOverviewDirtyChanged(isDirty: boolean): void;
  onOverviewSaved(): void;
  questions: Question[];
  survey: Survey;
  waves: SurveyWave[];
}) => {
  const isAdmin = useHasRole('admin');

  const { currentOrganizationId } = getOrgStatus();

  const { data: incidenceTypes = [], isLoading: isLoadingIncidenceTypes } =
    useQuery(incidenceTypeQueries.list);

  const { isLoadingTags, tags } = useTags({
    organizationId: survey.organizationId,
  });
  const initialTag = tags.find(({ id }) => id === survey.projectId);

  const { isLoadingOrgs, organizations } = useOrderedOrganizations();
  const initialSurveyOrg = organizations.find(({ value }) => {
    return value === currentOrganizationId;
  });

  const { isLoadingTeamMembers, orderedTeamMembers } = useOrderedTeamMembers({
    enabled: isAdmin,
    organizationId: survey.organizationId,
  });
  const { isLoadingUsers, users } = useAllUsers({
    enabled: isAdmin,
    role: 'admin',
  });
  const initialContact = orderedTeamMembers.find(
    ({ id }) => id === survey.userId,
  );
  const initialGlassContact = users.find(
    ({ id }) => id === survey.glassContactId,
  );

  const isLoading =
    isLoadingIncidenceTypes ||
    isLoadingTags ||
    isLoadingTeamMembers ||
    isLoadingUsers ||
    isLoadingOrgs;

  return isLoading ? (
    <SkeletonSurveyCard />
  ) : (
    <OverviewStepLoaded
      incidenceTypes={getIncidenceTypeOptions(incidenceTypes)}
      initialContact={initialContact}
      initialGlassContact={initialGlassContact}
      initialSurveyOrg={initialSurveyOrg}
      initialTag={initialTag}
      isAdmin={isAdmin}
      isShowingUnsavedChanges={isShowingUnsavedChanges}
      onDiscardChanges={onDiscardChanges}
      onDismissUnsavedChanges={onDismissUnsavedChanges}
      onHasError={onHasError}
      onOverviewDirtyChanged={onOverviewDirtyChanged}
      onOverviewSaved={onOverviewSaved}
      organizations={organizations}
      questions={questions}
      survey={survey}
      users={users}
      waves={waves}
    />
  );
};

export default OverviewStep;

const OverviewStepLoaded = ({
  incidenceTypes,
  initialContact,
  initialGlassContact,
  initialSurveyOrg,
  initialTag,
  isAdmin,
  onHasError,
  onOverviewSaved,
  organizations,
  survey,
  users,
  ...rest
}: Omit<OverviewFormProps, 'isSavingSurvey'> & {
  initialContact: TeamMember | undefined;
  initialGlassContact: UserV2 | undefined;
  initialSurveyOrg: ReactSelectValue<number> | undefined;
  initialTag: Tag | undefined;
  onOverviewSaved(): void;
  survey: Survey;
  users: UserV2[];
}) => {
  const [submitStep, setSubmitStep] = useState<
    'editing' | 'needsConfirmation' | 'readyToSubmit'
  >('editing');

  const { isPending: isSavingSurvey, mutateAsync: saveSurvey } = useSaveSurvey({
    onError: (err) => {
      onHasError();
      showErrorMessage({ err });
    },
    onSuccess: () => {
      setSubmitStep('editing');
      onOverviewSaved();
    },
  });

  const initialValues = apiDataToFormData({
    incidenceTypes,
    initialContact,
    initialGlassContact,
    initialSurveyOrg,
    initialTag,
    organizations,
    survey,
  });

  return (
    <Formik<SurveyOverviewFormData>
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(formData) => {
        if (
          survey.statusId === SURVEY_STATUSES.LIVE.id &&
          submitStep === 'editing'
        ) {
          setSubmitStep('needsConfirmation');
          return;
        }

        const data = formDataToApiData({
          formData: formData as SurveyOverviewFormDataValidated,
        });

        return saveSurvey({ data, surveyId: survey.id });
      }}
      validate={(formData) => {
        return validateOverviewData(formData, { isAdmin });
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <Form className="h-full" id="overview">
        <OverviewForm
          {...rest}
          incidenceTypes={incidenceTypes}
          isAdmin={isAdmin}
          isSavingSurvey={isSavingSurvey}
          onHasError={onHasError}
          organizations={organizations}
          users={users}
        />

        {submitStep === 'needsConfirmation' ? (
          <PutSurveyIntoDraftDialog
            formId="overview"
            isSaving={isSavingSurvey}
            onCloseModal={() => {
              setSubmitStep('editing');
            }}
          />
        ) : null}
      </Form>
    </Formik>
  );
};

const OverviewForm = ({
  incidenceTypes,
  isAdmin,
  isSavingSurvey,
  isShowingUnsavedChanges,
  onDiscardChanges,
  onDismissUnsavedChanges,
  onHasError,
  onOverviewDirtyChanged,
  organizations,
  users,
  waves,
}: OverviewFormProps) => {
  const { dirty } = useFormikContext();

  const [{ value: organizationIdSelect }] =
    useField<SurveyOverviewFormData['organizationId']>('organizationId');
  const [, , setContactHelpers] =
    useField<SurveyOverviewFormData['contact']>('contact');
  const [, , setTagHelpers] = useField<SurveyOverviewFormData['tag']>('tag');

  const organizationId = organizationIdSelect?.value;

  const { isLoadingTeamMembers, orderedTeamMembers } = useOrderedTeamMembers({
    enabled: isAdmin,
    organizationId,
  });

  // We only want to display survey contact options to the admin users if they have already selected the
  // organization to which the survey will belong.
  const teamMembers =
    isAdmin && !!organizationId
      ? orderedTeamMembers.map((teamMember) => {
          return createTeamMemberOption(teamMember);
        })
      : [];

  // Glass contact options are available to all admin users and use all users, not just team members
  const glassContactOptions = isAdmin
    ? users.map((user) => {
        return createUserOption(user);
      })
    : [];

  const { errors, onClickSubmit, validateAndSubmit } = useSubmitValidation({
    isSaving: isSavingSurvey,
    onHasError,
  });

  useEffect(() => {
    onOverviewDirtyChanged(dirty);
  }, [dirty, onOverviewDirtyChanged]);

  const gridClassess = 'grid grid-cols-[3fr_4fr]';

  return (
    <div>
      {isShowingUnsavedChanges && (
        <UnsavedChangesModal
          isSaving={isSavingSurvey}
          onClickDiscardChanges={onDiscardChanges}
          onClickSaveChanges={validateAndSubmit}
          onCloseModal={onDismissUnsavedChanges}
        />
      )}

      <div className="space-y-4">
        <SurveyStepStickyHeader>
          <h2 className="text-base font-semibold">Overview</h2>

          <ButtonLoading
            disabled={!dirty}
            hierarchy="primary"
            isLoading={isSavingSurvey}
            onClick={onClickSubmit}
            size="sm"
            // This can't currently be a submit button since we handle the form submission
            // in the onClickSubmit callback. If this is a "submit" button, it causes a double submission.
            type="button"
          >
            Save Changes
          </ButtonLoading>
        </SurveyStepStickyHeader>

        {errors && (
          <div className="mb-8">
            <FormErrorsAlert errors={getNestedErrorMessages(errors)} />
          </div>
        )}

        <div className={gridClassess}>
          <div>Survey Title</div>
          <FormInput
            id="title"
            labelFor="title"
            name="title"
            placeholder=" ex: Package testing study"
            size="md"
            type="text"
          />
        </div>

        {/*
         * If there's more than one wave, the number of respondents is added to the next wave,
         * not the survey. For a single wave survey it's here, because it's more intuitive if
         * you only ever have a single wave.
         */}
        {waves.length === 1 ? (
          <div className={gridClassess}>
            <div>Number of Respondents</div>
            <FormInput
              id="respondents"
              labelFor="respondents"
              name="respondents"
              placeholder="1000"
              size="md"
              type="number"
            />
          </div>
        ) : null}

        <div className={gridClassess}>
          <div>Estimated Incidence</div>
          <FormSearchSelectInput
            borderColor="#D9D9D9"
            inputId="incidenceType"
            labelFor="incidenceType"
            name="incidenceType"
            options={incidenceTypes}
          />
        </div>
        <div className={gridClassess}>
          <div>Tags (optional)</div>
          <TagSelect />
        </div>
        {organizations.length > 1 && (
          <div className={gridClassess}>
            <div>Organization</div>
            <FormSearchSelectInput
              borderColor="#D9D9D9"
              inputId="organization"
              labelFor="organization"
              name="organizationId"
              onChange={() => {
                // If an organization is changed, we load new options for the survey contact and tag so we want to clear out
                // the existing ones and make the user select an updated value.
                setContactHelpers.setValue(null);
                setTagHelpers.setValue(null);
              }}
              options={organizations}
            />
          </div>
        )}

        {isAdmin && (
          <>
            <h2 className="text-base font-semibold">Admin Settings</h2>
            <div className={gridClassess}>
              <div>Client Contact</div>
              <FormSearchSelectInput
                borderColor="#D9D9D9"
                inputId="contact"
                isDisabled={isLoadingTeamMembers}
                isLoading={isLoadingTeamMembers}
                labelFor="contact"
                name="contact"
                options={teamMembers}
              />
            </div>
            <div className={gridClassess}>
              <div>Glass Contact</div>
              <FormSearchSelectInput
                borderColor="#D9D9D9"
                inputId="glassContact"
                isDisabled={users.length === 0}
                isLoading={users.length === 0}
                labelFor="glassContact"
                name="glassContact"
                options={glassContactOptions}
              />
            </div>
            <div className={gridClassess}>
              <div>Survey Type</div>
              <FormSearchSelectInput
                borderColor="#D9D9D9"
                inputId="surveyType"
                labelFor="surveyType"
                name="surveyType"
                options={SURVEY_TYPE_OPTIONS}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TagSelect = () => {
  // The organization might be a form field if this is an admin filling out a survey for a different organization -
  // otherwise it will be the organization for the user.
  const [{ value: organizationIdSelect }] =
    useField<SurveyOverviewFormData['organizationId']>('organizationId');
  const organizationId = organizationIdSelect?.value;

  const [, , tagHelpers] = useField<SurveyOverviewFormData['tag']>('tag');

  const { isLoadingTags, onTagCreated, tags } = useTags({ organizationId });
  const tagOptions = tags.map((tag) => {
    return createTagOption(tag);
  });

  const { isPending: isCreatingTag, mutate: createTag } = useCreateTag({
    onError: (err) => {
      showErrorMessage({ err });
    },
    onSuccess: async (data) => {
      await onTagCreated();
      tagHelpers.setValue(createTagOption(data));
    },
  });

  return (
    <FormSearchSelectInput
      borderColor="#D9D9D9"
      inputId="tag"
      isClearable
      isCreatable={true}
      isDisabled={isLoadingTags || isCreatingTag}
      isLoading={isLoadingTags || isCreatingTag}
      labelFor="tag"
      name="tag"
      onCreateOption={(newTagName: string) => {
        if (!organizationId) {
          throw new Error(
            'Could not determine your organization. Please log out and log back in.',
          );
        }

        return createTag({ data: { organizationId, title: newTagName } });
      }}
      options={tagOptions}
    />
  );
};

function apiDataToFormData({
  incidenceTypes,
  initialContact,
  initialGlassContact,
  initialSurveyOrg,
  initialTag,
  organizations,
  survey,
}: {
  incidenceTypes: ReactSelectValue<IncidenceType>[];
  initialContact: TeamMember | undefined;
  initialGlassContact: UserV2 | undefined;
  initialSurveyOrg: ReactSelectValue<number> | undefined;
  initialTag: Tag | undefined;
  organizations: ReactSelectValue<number>[];
  survey: Survey;
}): SurveyOverviewFormData {
  // We could have an initial organization if the user is modifying the details of an existing survey
  // or if this is an admin that has changed organizations and is creating a new survey for that
  // organization.
  let organization = initialSurveyOrg;
  if (survey.organizationId) {
    organization = organizations.find(({ value }) => {
      return value === survey.organizationId;
    });
  }

  return {
    contact: initialContact ? createTeamMemberOption(initialContact) : null,
    glassContact: initialGlassContact
      ? createUserOption(initialGlassContact)
      : null,
    incidenceType:
      incidenceTypes.find(({ value }) => {
        return value.id === survey.incidenceTypeId;
      }) ?? null,
    organizationId: organization ?? null,
    respondents: survey.participants,
    surveyType: survey.surveyType
      ? createSurveyTypeOption(survey.surveyType)
      : null,
    tag: initialTag ? createTagOption(initialTag) : null,
    title: survey.title,
  };
}

function createTagOption(tag: Tag): ReactSelectValue<Tag> {
  return {
    label: tag.title,
    value: tag,
  };
}

function formDataToApiData({
  formData,
}: {
  formData: SurveyOverviewFormDataValidated;
}): UpdateSurveyRequestData {
  const {
    contact,
    glassContact,
    incidenceType,
    organizationId,
    respondents,
    surveyType,
    tag,
    title,
  } = formData;

  return {
    incidenceTypeId: incidenceType.value.id,
    organizationId: organizationId?.value,
    ownerId: contact?.value.id,
    participants: respondents,
    projectId: tag?.value.id ?? null,
    // Currently any edit to the survey details will cause the survey to be put back into
    // "Draft" status. This is due to the fact that changing some information could cause
    // a price change. In the future, this should be done on the backend and should only happen
    // if price-related properties are edited.
    statusId: 6,
    title,
    // New fields for update operations only
    glassContactId: glassContact?.value.id,
    surveyType: surveyType?.value.value,
  };
}

function validateOverviewData(
  formData: SurveyOverviewFormData,
  { isAdmin = false } = {},
): FormikErrors<SurveyOverviewFormData> {
  const errors: FormikErrors<SurveyOverviewFormData> = {};

  if (!formData.incidenceType) {
    errors.incidenceType = 'Please select an incidence type.';
  }

  if (!formData.organizationId) {
    errors.organizationId = 'Please select an organization.';
  }

  if (!formData.contact && isAdmin) {
    errors.contact = 'Please select a survey contact.';
  }

  if (!formData.respondents) {
    errors.respondents = 'Please enter the number of respondents.';
  }

  if (!formData.title) {
    errors.title = 'Please enter a title.';
  }

  // glassContact is optional - no validation required
  // surveyType is optional for backward compatibility - no validation required

  return errors;
}
